<script>
import {
  CdrText, IconLocationPinStroke, CdrList,
  IconCheckLg, IconXLg, IconMinusFill, IconCalendar,
} from '@rei/cedar';
import api from '../../utils/retailStoresSite.api';
import hoursMixin from '../../mixins/storeCommons';
import metrics from '../../mixins/metrics';

import StoreStatusComponent from '../commons/open-until/StoreStatusComponent.vue';
import MyStorePillComponent from '../commons/my-store/MyStorePillComponent.vue';

export default {
  name: 'StorePageHeading',
  components: {
    CdrText,
    IconLocationPinStroke,
    IconCheckLg,
    IconXLg,
    IconMinusFill,
    IconCalendar,
    CdrList,
    StoreStatusComponent,
    MyStorePillComponent,
  },
  mixins: [metrics, hoursMixin],
  props: {
    pageHeadingData: {
      type: Object,
      required: true,
    },
    storeData: {
      type: Object,
      required: true,
    },
    isMyStore: {
      type: Boolean,
      required: true,
    },
    isMyStoreToastOpen: {
      type: Boolean,
      required: true,
    },
    isEnabledMyStore: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      milesAway: '0',
      showMilesAway: false,
    };
  },
  computed: {
    displayMilesAway() {
      return this.showMilesAway;
    },
    displayOpenUntilCustomMessage() {
      return this.pageHeadingData.showOpenUntilCustomMessage;
    },
    getOpenUntilCustomMessage() {
      return this.pageHeadingData.openUntilCustomMessage;
    },
    inStoreShoppingOpenUntilModel() {
      const { pageHeadingData: { openUntilModel: { inStore } } } = this;
      return inStore;
    },
    curbsidePickupOpenUntilModel() {
      const { pageHeadingData: { openUntilModel: { curbside } } } = this;
      return curbside;
    },
    temporarilyClosedModel() {
      const { pageHeadingData: { openUntilModel: { temporarilyClosed } } } = this;
      return temporarilyClosed;
    },
  },
  mounted() {
    // Validate if geolocation is enabled and available for browser
    if ('geolocation' in navigator) {
      // getCurrentPosition works in IE11, but highAccuracy must be false, and maximumAge
      // must be set
      this.isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
      this.geolocationOptions = {
        timeout: 15000,
        enableHighAccuracy: !this.isIE11,
        maximumAge: this.isIE11 ? 60000 : 0,
      };
      navigator.geolocation.getCurrentPosition(
        this.onInitGeoLocationSuccess,
        this.onGeoLocationError,
        this.geolocationOptions
      );
    }
  },
  methods: {
    // Only used as placeholder in mount process
    onInitGeoLocationSuccess(position) {
      const payload = [
        {
          latitude: this.pageHeadingData.storeLatitude,
          longitude: this.pageHeadingData.storeLongitude,
        },
        {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        },
      ];
      api.postStoreDistance(payload).then((response) => {
        if (response.data && response.data.length > 0) {
          response.data.forEach((distance) => {
            if (distance.unit === 'mi') {
              this.milesAway = distance.distance.toFixed(2);
              this.showMilesAway = true;
              this.clientRendering();
            }
          });
        }
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('postStoreDistance Error: ', error);
      });
    },
    onGeoLocationError() {
      this.showMilesAway = false;
    },
    clientRendering() {
      const milesAwayTag = document.getElementById('milesAway');
      milesAwayTag.style.display = 'block';
      const milesAwayText = `${this.milesAway} miles away`;
      document.getElementById('milesAwayText').textContent = milesAwayText;
    },

  },
};
</script>

<template>
  <section class="home heading">
    <template v-if="showStoreSpecialMessageImportant">
      <!-- notes table -->
      <div class="addressBlock store-info-list heading__message">
        <cdr-list
          data-ui="important-message"
        >
          <li
            v-for="importantMessage in getSpecialMessagesImportant()"
            :key="importantMessage.name"
            class="importantMessage heading__message-text"
            v-html="getCustomerNotesHtml(importantMessage)"
          />
        </cdr-list>
      </div>
    </template>

    <div
      v-show="displayMilesAway"
      id="milesAway"
      class="milesAway heading__miles"
    >
      <icon-location-pin-stroke
        class="cdr-button__icon"
        inherit-color
      />
      <cdr-text
        id="milesAwayText"
        class="heading__miles-away"
      >
        &nbsp;{{ milesAway }} miles away
      </cdr-text>
    </div>

    <div
      class="heading__my-store"
    >
      <my-store-pill-component
        v-if="isEnabledMyStore && isMyStore"
        id="myStorePill"
        toast-id="myStoreToast"
        :is-open="isMyStoreToastOpen"
      />
    </div>

    <cdr-text
      tag="h1"
      class="heading__title"
      data-ui="store-hero-title"
    >
      REI {{ pageHeadingData.title }}
    </cdr-text>

    <div class="subheader">
      <div class="subheader__container">
        <store-status-component
          v-if="inStoreShoppingOpenUntilModel.show"
          title="In-store shopping"
          :description="inStoreShoppingOpenUntilModel.description"
          :icon-text="inStoreShoppingOpenUntilModel.available?'Available':'Unavailable'"
          data-ui="open-until"
        >
          <icon-check-lg
            v-if="inStoreShoppingOpenUntilModel.available"
            class="store-status__icon store-status__icon--green"
          />
          <icon-x-lg
            v-else
            class="store-status__icon store-status__icon--red"
          />
        </store-status-component>
        <store-status-component
          v-if="curbsidePickupOpenUntilModel.show"
          title="Curbside Pickup"
          :description="curbsidePickupOpenUntilModel.description"
          :icon-text="curbsidePickupOpenUntilModel.available?'Available':'Unavailable'"
          data-ui="curbside-pickup-info"
        >
          <icon-check-lg
            v-if="curbsidePickupOpenUntilModel.available"
            class="store-status__icon store-status__icon--green"
          />
          <icon-x-lg
            v-if="!curbsidePickupOpenUntilModel.available"
            class="store-status__icon store-status__icon--red"
          />
        </store-status-component>
        <store-status-component
          v-if="temporarilyClosedModel.show"
          :title="temporarilyClosedModel.description"
        >
          <icon-minus-fill
            class="store-status__icon store-status__icon--red"
          />
        </store-status-component>
        <div
          v-if="displayOpenUntilCustomMessage"
          class="custom_message__open-soon"
        >
          <icon-calendar
            class="cdr-button__icon subheader__icon-calendar"
            inherit-color
          />
          <span class="subheader__label">Opening soon</span>
          <cdr-text
            class="warning__text heading__miles-away"
          >
            {{ getOpenUntilCustomMessage }}
          </cdr-text>
        </div>
      </div>
    </div>
  </section>
</template>